import React, { useEffect } from "react";
import { X, Download, Play, FileText, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * MaterialViewer - A premium lightbox for playing videos and viewing collateral.
 */
const MaterialViewer = ({ isOpen, onClose, material }) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!isOpen || !material) return null;

  const isVideo = material.media_type === "video";
  const isImage = !isVideo && (
    material.url?.toLowerCase().endsWith(".jpg") ||
    material.url?.toLowerCase().endsWith(".jpeg") ||
    material.url?.toLowerCase().endsWith(".png") ||
    material.url?.toLowerCase().endsWith(".webp") ||
    material.file_type?.includes("image")
  );
  const isPdf = material.url?.toLowerCase().endsWith(".pdf") || material.file_type?.includes("pdf");

  const getEmbedUrl = (url) => {
    if (!url) return "";
    if (url.includes("youtube.com/watch?v=")) {
      return url.replace("watch?v=", "embed/");
    }
    if (url.includes("youtu.be/")) {
      return url.replace("youtu.be/", "www.youtube.com/embed/");
    }
    if (url.includes("vimeo.com/")) {
      const vimeoId = url.split("/").pop();
      return `https://player.vimeo.com/video/${vimeoId}`;
    }
    return url;
  };

  return (
    <div className="fixed inset-0 z-[100] flex flex-col bg-black/95 backdrop-blur-sm animate-in fade-in duration-300">
      {/* Header */}
      <div className="flex items-center justify-between px-6 h-16 border-b border-white/10 shrink-0">
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isVideo ? "bg-blue-500 text-white" : "bg-indigo-500 text-white"}`}>
            {isVideo ? <VideoIcon size={18} /> : <FileText size={18} />}
          </div>
          <div className="flex flex-col">
            <h3 className="text-sm font-bold text-white leading-none truncate max-w-[200px] md:max-w-md">
                {material.title}
            </h3>
            <span className="text-[10px] text-white/50 font-medium uppercase tracking-widest mt-1">
                {isVideo ? "Video Player" : isPdf ? "Document Viewer" : "Image Preview"}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {!isVideo && material.url && (
            <a
              href={material.url}
              target="_blank"
              rel="noopener noreferrer"
              download
              className="p-2.5 rounded-xl bg-white/5 text-white hover:bg-white/10 transition-all flex items-center gap-2 text-xs font-bold"
            >
              <Download size={18} />
              <span className="hidden sm:inline">Download</span>
            </a>
          )}
          <button
            onClick={onClose}
            className="p-2.5 rounded-xl bg-white/5 text-white hover:bg-white/10 transition-all ml-2"
          >
            <X size={20} />
          </button>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 relative flex items-center justify-center p-4 md:p-12 overflow-hidden">
        {isVideo ? (
          <div className="w-full max-w-5xl aspect-video rounded-2xl overflow-hidden shadow-2xl bg-black border border-white/5 animate-in zoom-in-95 duration-500">
            <iframe
              src={`${getEmbedUrl(material.url)}?autoplay=1`}
              className="w-full h-full"
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
        ) : isImage ? (
          <div className="relative max-w-full max-h-full group animate-in zoom-in-95 duration-500">
            <img
              src={material.url}
              alt={material.title}
              className="max-w-full max-h-[80vh] rounded-lg shadow-2xl object-contain border border-white/10"
            />
          </div>
        ) : isPdf ? (
             <div className="w-full h-full max-w-5xl bg-white rounded-2xl overflow-hidden shadow-2xl animate-in zoom-in-95 duration-500">
                <iframe
                    src={`${material.url}#toolbar=0`}
                    className="w-full h-full border-none"
                    title={material.title}
                ></iframe>
            </div>
        ) : (
          <div className="flex flex-col items-center gap-6 text-white/40 max-w-xs text-center">
            <div className="w-24 h-24 rounded-3xl bg-white/5 flex items-center justify-center">
               <FileText size={48} strokeWidth={1} />
            </div>
            <div className="space-y-2">
                <h4 className="text-lg font-bold text-white">Preview not available</h4>
                <p className="text-sm">This file type refers directly to a download. Please use the download button to view the material.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const VideoIcon = ({ size }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M15 10L19.5528 7.72361C20.2177 7.39115 21 7.87465 21 8.61803V15.382C21 16.1253 20.2177 16.6088 19.5528 16.2764L15 14M7 18H11C12.1046 18 13 17.1046 13 16V8C13 6.89543 12.1046 6 11 6H7C5.89543 6 5 6.89543 5 8V16C5 17.1046 5.89543 18 7 18Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

export default MaterialViewer;
